<template>
  <div>
    <InputWithValidation
      rules="required"
      :label="$t('profilePage.nom')"
      :model-value="user.nom"
      :placeholder="$t('profilePage.saisir-nom')"
      :disabled="readonly"
      :group="validationGroup"
      vid="nom"
      @update:model-value="debouncedUpdateUserField('nom', $event)"
      @manual-change="changeUtilisateur"
    />
    <InputWithValidation
      rules="required"
      :label="$t('profilePage.prenom')"
      :model-value="user.prenom"
      :placeholder="$t('profilePage.saisir-prenom')"
      :disabled="readonly"
      :group="validationGroup"
      vid="prenom"
      @update:model-value="debouncedUpdateUserField('prenom', $event)"
      @manual-change="changeUtilisateur"
    />
    <InputWithValidation
      rules="required|email"
      type="email"
      :label="$t('profilePage.courriel')"
      :model-value="user.courriel"
      :placeholder="$t('profilePage.saisir-courriel')"
      :input-notice="$t('profilePage.tooltip-courriel')"
      :disabled="readonly"
      :custom-error="errormessage"
      :group="validationGroup"
      vid="courriel"
      @update:model-value="debouncedUpdateUserField('courriel', $event)"
      @manual-change="changeUtilisateur"
    />
    <InputWithValidation
      rules="required|validPhoneNumber"
      type="tel"
      :label="$t('profilePage.telephone')"
      :model-value="user.telephone"
      :placeholder="$t('profilePage.saisir-telephone')"
      :input-notice="$t('profilePage.tooltip-telephone')"
      :disabled="readonly"
      :group="validationGroup"
      vid="telephone"
      @update:model-value="debouncedUpdateUserField('telephone', $event)"
      @manual-change="changeUtilisateur"
    />

    <div v-if="user.profil == PROFIL_UTILISATEUR.ENSEIGNANT">
      <b-field :label="$t(`profilePage.matieres.label`)">
        <b-select
          v-if="!readonly"
          :model-value="user.matiereEnseignee"
          :placeholder="$t(`profilePage.matieres.placeholder`)"
          :disabled="readonly"
          @update:model-value="updateUserField('matiereEnseignee', $event)"
        >
          <option></option>
          <template v-for="(option, i) in matieres" :key="option">
            <option :value="option">
              {{ matieresLabels[i] }}
            </option>
          </template>
          <optgroup :label="$t(`profilePage.matieres.langues-vivantes`)">
            <template v-for="(lv, i) in languesVivantes" :key="lv">
              <option :value="lv">
                {{ languesVivantesLabels[i] }}
              </option>
            </template>
          </optgroup>
          <optgroup :label="$t(`profilePage.matieres.langues-regionales`)">
            <template v-for="(lr, i) in languesRegionales" :key="lr">
              <option :value="lr">
                {{ languesRegionalesLabels[i] }}
              </option>
            </template>
          </optgroup>
          <option value="AUTRE">
            {{ $t(`profilePage.matieres.label-autre`) }}
          </option>
        </b-select>

        <b-input
          v-else
          :model-value="user.matiereEnseignee"
          :disabled="readonly"
          custom-class="SelectWithValidation-disabled"
        ></b-input>
      </b-field>
      <b-field :label="$t(`profilePage.etablissement.nom`)">
        <div style="display: flex; align-items: center">
          <b-input
            :model-value="etablissement"
            :disabled="true"
            rules="required"
            class="full-width-input"
            @update:model-value="updateEtablissement"
          />
          <b-button
            v-if="user.etablissement"
            :label="$t('profilePage.etablissement.consulter').toString()"
            type="is-primary"
            style="width: 50%"
            rounded
            @click="GoToEtablissement()"
          />
        </div>
      </b-field>

      <b-field v-if="user.matiereEnseignee == 'AUTRE'">
        <b-input
          :model-value="user.matiereEnseigneeAutre"
          :placeholder="$t(`profilePage.matieres.placeholder-autre`)"
          :disabled="readonly"
          rules="required"
          @update:model-value="updateUserField('matiereEnseigneeAutre', $event)"
        />
      </b-field>
    </div>

    <div v-if="user.profil == PROFIL_UTILISATEUR.REFERENT">
      <GroupCheckboxesWithValidation
        :label="$t('profilePage.competences-referent.title')"
        :group="validationGroup"
        rules="required"
        :model-value="checkedCompetenceStrings"
        :data="competencesStrings"
        :disabled="readonly"
        :vertical-display="true"
        @update:model-value="updateCheckedCompetences"
        @manual-change="changeCompetences"
      />
      <b-field :label="$t(`profilePage.structure.nom`)">
        <div style="display: flex; align-items: center">
          <b-input
            :model-value="structure"
            :disabled="true"
            rules="required"
            class="full-width-input"
            @update:model-value="updateStructure"
          />
          <b-button
            v-if="user.structure"
            :label="$t('profilePage.structure.consulter').toString()"
            type="is-primary"
            style="width: 50%"
            rounded
            @click="GoToStructure()"
          />
        </div>
      </b-field>
    </div>
    <div v-if="isRegionalUser">
      <InputWithValidation
        rules="required"
        :label="$t('profilePage.fonction')"
        :model-value="user.fonction"
        :placeholder="$t('profilePage.saisir-fonction')"
        :disabled="readonly"
        @update:model-value="updateUserField('fonction', $event)"
      />

      <InputWithValidation
        rules="required"
        :label="$t('profilePage.employeur')"
        :model-value="user.employeur"
        :placeholder="$t('profilePage.saisir-employeur')"
        :disabled="readonly"
        @update:model-value="updateUserField('employeur', $event)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import GroupCheckboxesWithValidation from "@/views/inputs/GroupCheckBoxesWithValidation.vue"
import { UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { MATIERES, PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { t } from "@/utils/i18n-utils"
import { BButton, BInput, BSelect, BField } from "buefy"
import { UsersService } from "@/services/rest/users/UsersService"

const COMPETENCES_STRINGS = {
  PEDAGOGIE: t("profilePage.competences-referent.pedagogie"),
  ECOLOGIE: t("profilePage.competences-referent.ecologie"),
  AE: t("profilePage.competences-referent.ae"),
}

export default defineComponent({
  name: "UserPersonnalInfo",
  components: {
    InputWithValidation,
    GroupCheckboxesWithValidation,
    BButton,
    BInput,
    BField,
    BSelect,
  },
  props: {
    user: {
      type: Object as PropType<UtilisateurDTO>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    validationGroup: {
      type: String,
      default: "",
    },
    errormessage: {
      type: String,
      default: "",
    },
  },

  emits: ["change-utilisateur", "close-modale"],
  data() {
    return {
      PROFIL_UTILISATEUR,
      etablissement: "",
      structure: "",
      idEtablissement: "",
      idStructure: "",
      matieres: Object.values(MATIERES).filter(
        (type) =>
          !type.startsWith("LANGUE_VIVANTE_") &&
          !type.startsWith("LANGUE_REGIONALE_") &&
          type != MATIERES.AUTRE,
      ),
      languesVivantes: Object.values(MATIERES).filter((type) => type.startsWith("LANGUE_VIVANTE_")),
      languesRegionales: Object.values(MATIERES).filter((type) =>
        type.startsWith("LANGUE_REGIONALE_"),
      ),
      competencesStrings: Object.values(COMPETENCES_STRINGS),
      checkedCompetenceStrings: [] as string[],
      debounceTimers: {} as Record<string, NodeJS.Timeout>,
    }
  },
  computed: {
    matieresLabels(): string[] {
      return this.matieres.map((type) => {
        return this.$t(
          `profilePage.matieres.label-${type.toLowerCase().replace(/_/g, "-")}`,
        ) as string
      })
    },
    languesVivantesLabels(): string[] {
      return this.languesVivantes.map((type) => {
        return this.$t(
          `profilePage.matieres.label-${type.toLowerCase().replace(/_/g, "-")}`,
        ) as string
      })
    },
    languesRegionalesLabels(): string[] {
      return this.languesRegionales.map((type) => {
        return this.$t(
          `profilePage.matieres.label-${type.toLowerCase().replace(/_/g, "-")}`,
        ) as string
      })
    },
    isRegionalUser(): boolean {
      return UsersService.INSTANCE.hasAnyProfile(this.user, [
        PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
        PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL,
        PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
      ])
    },
  },
  watch: {
    user(_newVal: string, _oldValue: string): void {
      this.init()
    },
  },
  created(): void {
    this.init()
  },
  methods: {
    init(): void {
      this.checkedCompetenceStrings = this.getCheckedCompetencesStrings()
      this.etablissement = this.user.etablissement ? this.user.etablissement.nom : ""
      this.structure = this.user.structure ? this.user.structure.nom : ""
      this.idEtablissement = this.user.etablissement?.id || ""
      this.idStructure = this.user.structure?.id || ""
    },
    updateUserField(fieldName: string, value: any): void {
      // Créer une copie profonde pour éviter la mutation directe
      const updatedUser = JSON.parse(JSON.stringify(this.user))

      // Modifier la copie
      ;(updatedUser as any)[fieldName] = value

      // Émettre au parent pour mise à jour
      this.$emit("change-utilisateur", updatedUser)
    },
    debouncedUpdateUserField(fieldName: string, value: any): void {
      // Annuler le timer précédent pour ce champ
      if (this.debounceTimers[fieldName]) {
        clearTimeout(this.debounceTimers[fieldName])
      }

      // Créer un nouveau timer
      this.debounceTimers[fieldName] = setTimeout(() => {
        this.updateUserField(fieldName, value)
        delete this.debounceTimers[fieldName]
      }, 100) // 100ms de débounce
    },
    updateCheckedCompetences(value: string[]): void {
      this.checkedCompetenceStrings = value
      this.changeCompetences()
    },
    updateEtablissement(value: string | number | undefined): void {
      this.etablissement = value !== undefined ? String(value) : ""
    },
    updateStructure(value: string | number | undefined): void {
      this.structure = value !== undefined ? String(value) : ""
    },
    changeCompetences(): void {
      // Créer une copie profonde pour éviter la mutation directe
      const updatedUser = JSON.parse(JSON.stringify(this.user))

      // Modifier les compétences sur la copie
      updatedUser.competencesReferentPedagogie = this.checkedCompetenceStrings.includes(
        COMPETENCES_STRINGS.PEDAGOGIE,
      )
      updatedUser.competencesReferentEcologie = this.checkedCompetenceStrings.includes(
        COMPETENCES_STRINGS.ECOLOGIE,
      )
      updatedUser.competencesReferentAe = this.checkedCompetenceStrings.includes(
        COMPETENCES_STRINGS.AE,
      )

      // Émettre au parent pour mise à jour
      this.$emit("change-utilisateur", updatedUser)
    },
    emitCloseModale(): void {
      this.$emit("close-modale")
    },
    GoToStructure(): void {
      this.$router.push("/structure/" + this.idStructure + "/projets")
      this.emitCloseModale()
    },
    GoToEtablissement(): void {
      this.$router.push("/etablissement/" + this.idEtablissement + "/projets")
      this.emitCloseModale()
    },
    getCheckedCompetencesStrings(): string[] {
      const competences: string[] = []
      if (this.user.competencesReferentPedagogie) {
        competences.push(COMPETENCES_STRINGS.PEDAGOGIE)
      }
      if (this.user.competencesReferentEcologie) {
        competences.push(COMPETENCES_STRINGS.ECOLOGIE)
      }
      if (this.user.competencesReferentAe) {
        competences.push(COMPETENCES_STRINGS.AE)
      }
      return competences
    },
    changeUtilisateur(): void {
      // Créer une copie profonde pour éviter la mutation
      const updatedUser = JSON.parse(JSON.stringify(this.user))
      this.$emit("change-utilisateur", updatedUser)
    },
  },
})
</script>
<style>
.full-width-input {
  width: 50%;
  flex: 1;
  margin-right: 10px;
}
</style>
